import { useEffect, useMemo, useState } from "react";

interface UseSearchParamsReturn {
  get: <T = string>(key: string) => T | null;
  getAll: (key: string) => string[];
  has: (key: string) => boolean;

  toString: () => string;
  toObject: () => Record<string, string>;
  size: number;

  searchParams: URLSearchParams;
}

export function useSearchParams(): UseSearchParamsReturn {
  const [search, setSearch] = useState(() => window.location.search);

  useEffect(() => {
    const handleLocationChange = () => {
      setSearch(window.location.search);
    };

    window.addEventListener("popstate", handleLocationChange);

    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function (...args) {
      originalPushState.apply(window.history, args);
      handleLocationChange();
    };

    window.history.replaceState = function (...args) {
      originalReplaceState.apply(window.history, args);
      handleLocationChange();
    };

    return () => {
      window.removeEventListener("popstate", handleLocationChange);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, []);

  const searchParams = useMemo(() => {
    return new URLSearchParams(search);
  }, [search]);

  const get = <T = string>(key: string): T | null => {
    const value = searchParams.get(key);
    if (value === null) return null;
    try {
      return JSON.parse(value) as T;
    } catch {
      return value as unknown as T;
    }
  };

  const getAll = (key: string): string[] => {
    return searchParams.getAll(key);
  };

  const has = (key: string): boolean => {
    return searchParams.has(key);
  };

  const toString = (): string => {
    return searchParams.toString();
  };

  const toObject = (): Record<string, string> => {
    const obj: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      obj[key] = value;
    });
    return obj;
  };

  const size = useMemo(() => {
    return Array.from(searchParams.keys()).length;
  }, [searchParams]);

  return {
    get,
    getAll,
    has,
    toString,
    toObject,
    size,
    searchParams
  };
}

export const parseParam = {
  string: (value: string | null, defaultValue = ""): string => {
    return value ?? defaultValue;
  },

  number: (value: string | null, defaultValue = 0): number => {
    if (!value) return defaultValue;
    const parsed = Number(value);
    return isNaN(parsed) ? defaultValue : parsed;
  },

  boolean: (value: string | null, defaultValue = false): boolean => {
    if (!value) return defaultValue;
    return value.toLowerCase() === "true";
  },

  array: (
    value: string | null,
    separator = ",",
    defaultValue: string[] = []
  ): string[] => {
    if (!value) return defaultValue;
    return value.split(separator).filter(Boolean);
  },

  json: <T>(value: string | null, defaultValue: T): T => {
    if (!value) return defaultValue;
    try {
      return JSON.parse(value);
    } catch {
      return defaultValue;
    }
  }
};
